import { checkProxy, URL } from "@neru/base/utils";
import { Spinner } from "@neru/ui";
import React, { Component, type ErrorInfo } from "react";
import ReactDOMClient from "react-dom/client";
import singleSpaReact from "single-spa-react";

import Bootstrap from "./Bootstrap";
import { config } from "./utils/config";

interface StoreChangeEventData {
  storeId: string;
}

interface SinglePageAppProps {
  routerBase: string;
  userStore?: {
    $subscribe: (callback: (data: StoreChangeEventData) => void) => void;
    info: MFA.UserInfo;
  };
}

interface SinglePageAppState {
  hasError: boolean;
  isProxyCheckComplete: boolean;
  userInfo: MFA.UserInfo;
}

class SinglePageApp extends Component<SinglePageAppProps, SinglePageAppState> {
  state: SinglePageAppState = {
    hasError: false,
    isProxyCheckComplete: false,
    userInfo: { status: "pending" },
  };

  constructor(props: SinglePageAppProps) {
    super(props);
    config.setBasePath(props.routerBase);
  }

  handlePropsUpdate = () => {
    if (!this.props.userStore) return;

    this.setState({ userInfo: this.props.userStore.info });
  };

  async componentDidMount() {
    try {
      await checkProxy();
      this.setState({ isProxyCheckComplete: true }); // Update state when proxy check is complete
    } catch (error) {
      console.error("Proxy check failed", error);
    }

    if (!this.props.userStore) return;

    this.setState({ userInfo: this.props.userStore.info });

    this.props.userStore.$subscribe(({ storeId }: StoreChangeEventData) => {
      if (storeId === "user") {
        this.handlePropsUpdate();
      }
    });
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(error, info);
    this.setState({ hasError: true });
  }

  render() {
    if (!URL.isWhitelistedDomain(window.location.hostname)) {
      return (
        <div>
          <h3>Forbidden</h3>
          <p>Please contact support.</p>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div>
          <h3>Something went wrong!</h3>
          <p>Try reloading the page.</p>
        </div>
      );
    }

    if (!this.state.isProxyCheckComplete) {
      return <Spinner data-testid="proxy-check" />;
    }

    return (
      <div className="vvd-root vivid-scope neru">
        <Bootstrap userInfo={this.state.userInfo} />
      </div>
    );
  }
}

const reactLifecycles = singleSpaReact({
  React,
  ReactDOMClient,
  rootComponent: SinglePageApp,
});

export const bootstrap = reactLifecycles.bootstrap;
export const mount = reactLifecycles.mount;
export const unmount = reactLifecycles.unmount;

export default reactLifecycles;
